/**
 * Password helpers — self-service change (My Account) and the owner's
 * "set new password" on /sessions (server side in api/_sessions.js).
 */
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { currentSessionId, sessionsApi } from './deviceSession';

const MIN_PASSWORD = 6; // Firebase's own minimum

/** '' when OK, otherwise the message to show. */
export function validateNewPassword(next, confirm, current = null) {
  if (!next || next.length < MIN_PASSWORD) return `New password must be at least ${MIN_PASSWORD} characters.`;
  if (next.length > 128) return 'New password is too long.';
  if (confirm !== undefined && confirm !== null && next !== confirm) return 'The two new passwords don’t match.';
  if (current !== null && next === current) return 'New password must be different from the current one.';
  return '';
}

// Menu words + digits: easy to read out or text to a staff member.
const WORDS = ['Mango', 'Dosa', 'Chai', 'Masala', 'Paneer', 'Tikka', 'Idly', 'Vada', 'Lassi', 'Curry', 'Samosa', 'Biryani', 'Pesarattu', 'Upma', 'Rasam', 'Kulfi'];
const SYMBOLS = '!@#$%&*?';

/**
 * Uniform number in [0, 1) from the OS's cryptographic RNG. Every browser the
 * app supports has it; the fallback only exists for old test environments.
 */
function secureRandom() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  if (!c?.getRandomValues) return Math.random();
  const buf = new Uint32Array(1);
  c.getRandomValues(buf);
  return buf[0] / 2 ** 32;
}

/**
 * Readable temporary password, e.g. "Chai-Kulfi-4827!". Meant to be replaced:
 * the owner's "set password" requires a new one at next sign-in by default.
 */
export function generatePassword(rand = secureRandom) {
  const pick = (arr) => arr[Math.floor(rand() * arr.length)];
  const digits = String(Math.floor(rand() * 9000) + 1000);
  return `${pick(WORDS)}-${pick(WORDS)}-${digits}${pick(SYMBOLS)}`;
}

const AUTH_MESSAGES = {
  'auth/wrong-password': 'Current password is incorrect.',
  'auth/invalid-credential': 'Current password is incorrect.',
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
  'auth/weak-password': `New password is too weak (at least ${MIN_PASSWORD} characters).`,
  'auth/requires-recent-login': 'Please sign out and back in, then try again.',
  'auth/network-request-failed': 'No connection. Check your internet and try again.',
  'auth/user-disabled': 'This account has been blocked. Contact the owner.',
};
export const authMessage = (err, fallback = 'Something went wrong. Please try again.') =>
  AUTH_MESSAGES[err?.code] || fallback;

/**
 * Change the signed-in user's own password. Firebase requires a recent
 * sign-in, so the current password is checked first. Firebase then revokes
 * the account's other devices; `end-others` makes them sign out right away.
 * Resolves to the number of other devices signed out (null if unknown).
 */
export async function changeOwnPassword(user, current, next) {
  const cred = EmailAuthProvider.credential(user.email, current);
  await reauthenticateWithCredential(user, cred);
  await updatePassword(user, next);
  try {
    const r = await sessionsApi(user, { action: 'end-others', currentSessionId: currentSessionId() }, { forceRefresh: true });
    return r.devices ?? 0;
  } catch {
    return null; // the password DID change; other devices drop within the hour
  }
}
